import { sendVerificationCode } from "./apiAccessRequest"

export const API_URL = window.env?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '/api'
export const ORIGIN = process.env.REACT_APP_ORIGIN || 'fanar'


const getAuthPath = () => `${API_URL}/auth`
const getUserPath = () => `${API_URL}/users`
const getProjectPath = (projectId?: string) => `${API_URL}/projects${projectId ? `/${projectId}` : ''}`
const getChatPath = (chatId?: string, shared?: boolean) => `${API_URL}/${shared ? 'shared-' : ''}chats${chatId ? `/${chatId}` : ''}`
const getChatMessagePath = (chatId?: string, chatMessageId?: number, shared?: boolean) => `${getChatPath(chatId, shared)}/messages${chatMessageId? `/${chatMessageId}` : ''}`
const getApiAccessRequestPath = () => `${API_URL}/api-access-requests`
const getAdminPath = () => `${API_URL}/admin`

export const endpoints = {
  auth: {
    signIn: () => `${getAuthPath()}/sign-in`,
    signOut: () => `${getAuthPath()}/sign-out`,
    sendVerificationCode: () => `${getAuthPath()}/verification-code`,
    getStatus: () => `${getAuthPath()}/status`,
    verifyVerificationCode: () => `${getAuthPath()}/verification-code/verify`,
    setPassword: () => `${getAuthPath()}/password`,
    getGuestSession: () => `${getAuthPath()}/guest-session/${ORIGIN}`,
  },
  user: {
    getProfile: () => `${getUserPath()}/me`
  },
  project: {
    getAll: () => getProjectPath(),
    getOne: (projectId: string) => getProjectPath(projectId),
    updateSetting: (projectId: string, type: string) => `${getProjectPath(projectId)}/${type}/setting`,
    regenerateAPIAccess: (projectId: string) => `${getProjectPath(projectId)}/regenerate-api-access`,
    getAPIRequestLogs: (projectId: string) => `${getProjectPath(projectId)}/api-request-logs`,
    getModels: (projectId: string) => `${getProjectPath(projectId)}/models`,
  },
  chat: {
    getAll: () => getChatPath(),
    deleteAll: () => getChatPath(),
    updateName: (chatId: string) => getChatPath(chatId),
    delete: (chatId: string) => getChatPath(chatId),
    getShared: (chatId?: string, sharedChatId?: string) => `${getChatPath(chatId ? chatId : sharedChatId, sharedChatId !== undefined)}${sharedChatId ? '' : '/share'}`,
    share: (chatId: string) => `${getChatPath(chatId)}/share`,
    download: (chatId: string) => `${getChatPath(chatId)}/download`,
    recognizeSpeech: (chatId: string) => `${getChatPath(chatId)}/recognize-speech`,
  },
  chatMessage: {
    getAll: (chatId: string, shared: boolean) => getChatMessagePath(chatId, undefined, shared),
    sendAndGetResponse: (chatId: string) => getChatMessagePath(chatId, undefined),
    getResponse: (chatId: string, chatMessageId: number) => getChatMessagePath(chatId, chatMessageId),
    isAttributable: (chatId: string, chatMessageId: number) => `${getChatMessagePath(chatId, chatMessageId)}/is-attributable`,
    attribute: (chatId: string, chatMessageId: number) => `${getChatMessagePath(chatId, chatMessageId)}/attribute`,
    delete: (chatId: string, chatMessageId: number) => getChatMessagePath(chatId, chatMessageId),
    getFile: (chatId: string, chatMessageId: number, type: string, shared: boolean) => `${getChatMessagePath(chatId, chatMessageId, shared)}/file/${type}`,
    diacritize: (chatId: string, chatMessageId: number, isSystemMessage: boolean) => `${getChatMessagePath(chatId, chatMessageId)}/diacritize?for_system_message=${isSystemMessage}`,
    generateFile: (chatId: string, chatMessageId: number, type: string, isSystemMessage: boolean) => `${getChatMessagePath(chatId, chatMessageId)}/generate/${type}?for_system_message=${isSystemMessage}`,
    getUploadedFile: (chatId: string, chatUploadedFileId: string, shared: boolean) => `${getChatPath(chatId, shared)}/uploaded-files/${chatUploadedFileId}`,
    searchImages: (chatId: string, chatMessageId: number, isSystemMessage: boolean) => `${getChatMessagePath(chatId, chatMessageId)}/search/images?for_system_message=${isSystemMessage}`,
    translate: (chatId: string, chatMessageId: number, isSystemMessage: boolean) => `${getChatMessagePath(chatId, chatMessageId)}/translate?for_system_message=${isSystemMessage}`,
    react: (chatId: string, chatMessageId: number) => `${getChatMessagePath(chatId, chatMessageId)}/react`,
    submitFeedback: (chatId: string, chatMessageId: number) => `${getChatMessagePath(chatId, chatMessageId)}/feedback`,
  },
  apiAccessRequest: {
    getAll: () => getApiAccessRequestPath(),
    updateStatus: (apiAccessRequestId: number, status: string) => `${getApiAccessRequestPath()}/${apiAccessRequestId}/${status}`,
    sendVerificationCode: () => `${getApiAccessRequestPath()}/send-verification-code`,
  },
  admin:{
    hasPermission: () => `${getAdminPath()}/has-permission`,
    getUsers: () => `${getAdminPath()}/users`,
    updateUserType: () => `${getAdminPath()}/users/type`,
    getOTPSentHistory: () => `${getAdminPath()}/otp-sent-history`,
    getProject: (projectId: string) => `${getAdminPath()}/projects/${projectId}`,
    getProjects: () => `${getAdminPath()}/projects`,
    getChatMessages: () => `${getAdminPath()}/chat-messages`,
    getChatMessageProjects: () => `${getAdminPath()}/chat-messages/projects`,
    getChatMessageUsers: () => `${getAdminPath()}/chat-messages/users`,
    getChatMessageModelIDs: () => `${getAdminPath()}/chat-messages/model-ids`,
    getContentInterventions: () => `${getAdminPath()}/content-interventions`,
    addContentInterventions: () => `${getAdminPath()}/content-interventions`,
    deleteContentIntervention: (interventionId: number) => `${getAdminPath()}/content-interventions/${interventionId}`,
    deleteAllContentInterventions: () => `${getAdminPath()}/content-interventions`,
    getContentBlocklists: (type: string) => `${getAdminPath()}/content-blocklists/${type}`,
    addContentBlocklist: (type: string) => `${getAdminPath()}/content-blocklists/${type}`,
    deleteContentBlocklist: (type: string, blocklistId: number) => `${getAdminPath()}/content-blocklists/${type}/${blocklistId}`,
    deleteAllContentBlocklists: (type: string) => `${getAdminPath()}/content-blocklists/${type}`,
    getContentIslamic: () => `${getAdminPath()}/content-islamic`,
    addContentIslamic: () => `${getAdminPath()}/content-islamic`,
    deleteContentIslamic: (islamicId: number) => `${getAdminPath()}/content-islamic/${islamicId}`,
    deleteAllContentIslamic: () => `${getAdminPath()}/content-islamic`,
    getPromptClassifications: () => `${getAdminPath()}/prompt-classifications`,
    getPromptInterventionSimilarities: () => `${getAdminPath()}/prompt-intervention-similarities`,
    getFilteredContent: () => `${getAdminPath()}/filtered-content`,
    getContentSafetyScores: () => `${getAdminPath()}/content-safety-scores`,
  }
};